import { api } from './api'
import { Certificate, CertificateFormData } from '../types/certificate'

export const certificateService = {
  /**
   * Get all certificates
   */
  getAll: async (): Promise<Certificate[]> => {
    const response = await api.get<Certificate[]>('/certificates')
    return response.data
  },

  /**
   * Get certificates by type
   */
  getByType: async (type: Certificate['type']): Promise<Certificate[]> => {
    const response = await api.get<Certificate[]>(`/certificates?type=${type}`)
    return response.data
  },

  /**
   * Get a single certificate by ID
   */
  getById: async (id: string): Promise<Certificate> => {
    const response = await api.get<Certificate>(`/certificates/${id}`)
    return response.data
  },

  /**
   * Upload and create a new certificate
   */
  create: async (data: CertificateFormData): Promise<Certificate> => {
    // Convert files to base64
    const formData = new FormData()
    formData.append('type', data.type)
    formData.append('name', data.name)
    if (data.description) formData.append('description', data.description)
    if (data.certificateFile) formData.append('certificate', data.certificateFile)
    if (data.privateKeyFile) formData.append('privateKey', data.privateKeyFile)
    if (data.password) formData.append('password', data.password)
    if (data.notes) formData.append('notes', data.notes)

    const response = await api.post<Certificate>('/certificates', formData)
    return response.data
  },

  /**
   * Update an existing certificate
   */
  update: async (id: string, data: Partial<CertificateFormData>): Promise<Certificate> => {
    if (data.certificateFile || data.privateKeyFile) {
      const formData = new FormData()
      if (data.name) formData.append('name', data.name)
      if (data.description) formData.append('description', data.description)
      if (data.certificateFile) formData.append('certificate', data.certificateFile)
      if (data.privateKeyFile) formData.append('privateKey', data.privateKeyFile)
      if (data.password) formData.append('password', data.password)
      if (data.notes) formData.append('notes', data.notes)

      const response = await api.put<Certificate>(`/certificates/${id}`, formData)
      return response.data
    }

    // Simple update without files
    const response = await api.put<Certificate>(`/certificates/${id}`, {
      name: data.name,
      description: data.description,
      password: data.password,
      notes: data.notes,
    })
    return response.data
  },

  /**
   * Delete a certificate
   */
  delete: async (id: string): Promise<void> => {
    await api.delete(`/certificates/${id}`)
  },

  /**
   * Activate a certificate
   */
  activate: async (id: string): Promise<Certificate> => {
    const response = await api.post<Certificate>(`/certificates/${id}/activate`)
    return response.data
  },

  /**
   * Revoke a certificate
   */
  revoke: async (id: string): Promise<Certificate> => {
    const response = await api.post<Certificate>(`/certificates/${id}/revoke`)
    return response.data
  },

  /**
   * Validate certificate
   */
  validate: async (id: string): Promise<{ valid: boolean; message?: string }> => {
    const response = await api.post<{ valid: boolean; message?: string }>(`/certificates/${id}/validate`)
    return response.data
  },

  /**
   * Check if PCRS login credentials are configured (stored in Secrets Manager).
   * Never returns the actual values.
   */
  getPcrsCredentialsStatus: async (): Promise<{ hasCredentials: boolean }> => {
    const response = await api.get<{ hasCredentials: boolean }>('/certificates/pcrs-credentials')
    return response.data
  },

  /**
   * Store PCRS login credentials in Secrets Manager.
   */
  updatePcrsCredentials: async (username: string, password: string): Promise<void> => {
    await api.put('/certificates/pcrs-credentials', { username, password })
  },
}

